import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

export interface Device {
  _id: string;
  jssid?: number;
  name?: string;
  serial?: string;
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  };
}

interface DeviceContextType {
  selectedDevice: Device | null;
  allDevices: Device[];
  isLoading: boolean;
  error: string | null;
  selectDevice: (device: Device) => void;
  clearDevice: () => void;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const useDevice = () => {
  const context = useContext(DeviceContext);
  if (context === undefined) {
    throw new Error('useDevice must be used within a DeviceProvider');
  }
  return context;
};

interface DeviceProviderProps {
  children: ReactNode;
}

// Helper to get serial from header injection (window variable or meta tag)
const getSerialFromHeader = (): string | null => {
  if (typeof window === 'undefined') return null;
  
  // Check window variable (injected by server/native app)
  const windowSerial = (window as any).__DEVICE_SERIAL__;
  if (windowSerial) return windowSerial;
  
  // Check meta tag (injected by server/proxy)
  const metaTag = document.querySelector('meta[name="x-device-serial"]');
  if (metaTag) {
    const content = metaTag.getAttribute('content');
    if (content) return content;
  }
  
  return null;
};

export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [error, setError] = useState<string | null>(null);
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Get device ID from URL parameter (treat as serial)
  const deviceId = params.deviceId;
  
  // Get device serial with priority: header > query > path > localStorage > null (NO default!)
  const serialFromHeader = getSerialFromHeader();
  const serialFromQuery = searchParams.get('cx__serial');
  const storedSerial = typeof window !== 'undefined' ? localStorage.getItem('device_serial') : null;
  const deviceSerial = serialFromHeader || serialFromQuery || deviceId || storedSerial || null;

  const serialSource = serialFromHeader ? 'header' : serialFromQuery ? 'query' : deviceId ? 'path' : storedSerial ? 'localStorage' : 'none';

  // Log what the device is sending
  console.log('🔍 DeviceContext - Serial Resolution:', {
    serial: deviceSerial,
    source: serialSource,
    'x-device-serial': serialFromHeader || 'not provided',
    cx__serial: serialFromQuery || 'not provided',
    deviceId: deviceId || 'not provided',
    localStorage: storedSerial || 'not set'
  });

  // Create device object from URL parameter - only if we have a valid serial
  useEffect(() => {
    if (!deviceSerial) {
      // No serial available - clear device and let DeviceSerialGate handle the error
      setSelectedDevice(null);
      setError('No device serial found');
      console.log('DeviceContext - No device serial available');
      return;
    }
    
    const effectiveDeviceId = deviceId || deviceSerial;
    
    const device: Device = {
      _id: effectiveDeviceId,
      serial: deviceSerial,
      name: `Device ${deviceSerial}`,
    };
    
    setSelectedDevice(device);
    localStorage.setItem('selected_device_id', effectiveDeviceId);
    localStorage.setItem('device_serial', deviceSerial);
    setError(null);
    
    console.log('DeviceContext - Using device serial:', deviceSerial);
  }, [deviceId, deviceSerial]);

  const selectDevice = (device: Device) => {
    setSelectedDevice(device);
    localStorage.setItem('selected_device_id', device._id);
    setError(null);
  };

  const clearDevice = () => {
    setSelectedDevice(null);
    localStorage.removeItem('selected_device_id');
    localStorage.removeItem('device_serial');
  };

  const value: DeviceContextType = {
    selectedDevice,
    allDevices: selectedDevice ? [selectedDevice] : [], // Only return current device
    isLoading: false, // No loading since we're not fetching
    error,
    selectDevice,
    clearDevice,
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
};